import { User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { DiscussionMessage as DiscussionMessageType } from "@/types";

interface DiscussionMessageProps {
  message: DiscussionMessageType;
  isOwnMessage: boolean;
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function MentionText({ children }: { children: string }) {
  const mentionRegex = /@([^\s@]+)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  children.replace(mentionRegex, (match, _email, offset) => {
    if (offset > lastIndex) {
      parts.push(children.slice(lastIndex, offset));
    }
    parts.push(
      <span key={key++} className="text-violet-400 font-medium">
        {match}
      </span>
    );
    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < children.length) {
    parts.push(children.slice(lastIndex));
  }

  return <>{parts.length > 0 ? parts : children}</>;
}

function MarkdownWithMentions({ content }: { content: string }) {
  return (
    <div className="prose prose-invert prose-sm max-w-none break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:underline">
              {children}
            </a>
          ),
          code: ({ className, children, ...props }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <code className="bg-slate-900/50 px-1 py-0.5 rounded text-xs" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-slate-900/50 rounded p-2 my-2 overflow-x-auto text-xs">
              {children}
            </pre>
          ),
          ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-slate-500 pl-3 my-2 italic text-slate-300">
              {children}
            </blockquote>
          ),
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em>{children}</em>,
          text: ({ children }) => {
            if (typeof children === "string") {
              return <MentionText>{children}</MentionText>;
            }
            return <>{children}</>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export function DiscussionMessage({ message, isOwnMessage }: DiscussionMessageProps) {
  const profile = message.profiles;
  const displayName = profile?.full_name || "Unknown User";
  const avatarUrl = profile?.avatar_url;

  if (isOwnMessage) {
    return (
      <div className="flex flex-row-reverse gap-3">
        <div className="flex-shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full border bg-[var(--avatar-user-bg)] border-[var(--avatar-user-border)] flex items-center justify-center dark:bg-indigo-600 dark:border-transparent">
              <User className="h-4 w-4 text-[var(--avatar-user-fg)] dark:text-white" />
            </div>
          )}
        </div>
        <div className="flex flex-col items-end max-w-[75%]">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-slate-400">{formatTime(message.created_at)}</span>
            <span className="text-xs font-medium text-slate-300">{displayName}</span>
          </div>
          <div className="bg-[var(--bubble-user-bg)] text-[var(--bubble-user-fg)] border border-[var(--bubble-user-border)] rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm dark:bg-indigo-600 dark:text-white dark:border-transparent">
            <MarkdownWithMentions content={message.content} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center">
            <User className="h-4 w-4 text-white" />
          </div>
        )}
      </div>
      <div className="flex flex-col max-w-[75%]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-slate-300">{displayName}</span>
          <span className="text-xs text-slate-400">{formatTime(message.created_at)}</span>
        </div>
        <div className="bg-slate-700 text-slate-100 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm">
          <MarkdownWithMentions content={message.content} />
        </div>
      </div>
    </div>
  );
}
